import CategoryIcon from "@mui/icons-material/Category";
import Chip from "@mui/material/Chip";
import {
  BooleanInput,
  Create,
  CreateButton,
  Datagrid,
  Edit,
  FunctionField,
  List,
  NumberInput,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  TopToolbar,
  required,
  usePermissions,
  type CreateProps,
  type EditProps,
  type ListProps,
} from "react-admin";
import CustomEmpty from "@/components/custom-empty";
import { hasPermission } from "@/utils/permission-utils";
import { MADRASA_CLASSES_CREATE } from "../shared/madrasa-permissions";
import { RecordTitle } from "../shared/RecordTitle";

function ClassFields() {
  return (
    <>
      <TextInput
        source="name"
        label="Name"
        validate={required()}
        helperText='For example "Class 3" or "Hifz A". Add the section to the name.'
        fullWidth
      />
      <NumberInput
        source="sortOrder"
        label="Sort order"
        defaultValue={0}
        min={0}
        helperText="Lower numbers are listed first."
        fullWidth
      />
      <BooleanInput
        source="isActive"
        label="Active"
        defaultValue
        helperText="Turn off for classes that are no longer running instead of deleting them."
      />
    </>
  );
}

const ListActions = () => {
  const { permissions } = usePermissions();
  return (
    <TopToolbar>
      {hasPermission(permissions, MADRASA_CLASSES_CREATE) && <CreateButton />}
    </TopToolbar>
  );
};

const listFilters = [
  <SelectInput
    key="isActive"
    source="isActive"
    label="Status"
    choices={[
      { id: "true", name: "Active" },
      { id: "false", name: "Inactive" },
    ]}
    alwaysOn
  />,
];

export function MadrasaClassesList(props: ListProps) {
  return (
    <List
      {...props}
      sort={{ field: "sortOrder", order: "ASC" }}
      perPage={50}
      filters={listFilters}
      actions={<ListActions />}
      empty={
        <CustomEmpty
          title="No classes yet"
          subtitle="Create the classes your madrasa runs, then enroll students into them."
          action={<CreateButton variant="contained" />}
        />
      }
    >
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="name" label="Name" />
        <TextField source="sortOrder" label="Sort order" />
        <FunctionField
          label="Status"
          render={(record: { isActive?: boolean }) =>
            record.isActive ? (
              <Chip size="small" color="success" label="Active" />
            ) : (
              <Chip size="small" label="Inactive" />
            )
          }
        />
      </Datagrid>
    </List>
  );
}

export function MadrasaClassesCreate(props: CreateProps) {
  return (
    <Create {...props} redirect="list" title="Add class">
      <SimpleForm warnWhenUnsavedChanges sx={{ maxWidth: 500 }}>
        <ClassFields />
      </SimpleForm>
    </Create>
  );
}

export function MadrasaClassesEdit(props: EditProps) {
  return (
    <Edit
      {...props}
      mutationMode="pessimistic"
      title={<RecordTitle prefix="Edit class" sources={["name"]} />}
    >
      <SimpleForm warnWhenUnsavedChanges sx={{ maxWidth: 500 }}>
        <ClassFields />
      </SimpleForm>
    </Edit>
  );
}

export default {
  list: MadrasaClassesList,
  create: MadrasaClassesCreate,
  edit: MadrasaClassesEdit,
  icon: CategoryIcon,
  label: "Classes",
  options: { label: "Classes" },
  name: "madrasaClasses",
};
